import type { Periods } from '@app/src/components/CreateNewRequestModal/PeriodSelector/PeriodSelector'
import type { FilterGroup } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import type { ReportFlagRule, ReportFlagType } from '@app/src/types/flags'
import type { SustainabilityTopic } from '@app/src/types/topicalRisk'
import type { AccountType, RequestTags, ResourceTypes, Role } from '@app/src/wf-constants'
import type { ButtonProps } from '@mui/material'
import type { RequestAutomation } from './automations'
import type { FinalRiskRating } from './categories'
import type { GenericOrganization, Investee, Organization, Provider, Supplier } from './organizations'
import type { Product } from './product'

export interface RelatedResource {
  type: string
  url: string
}

export interface ResourceType {
  id: number
  image?: Image
  name: string
  linked?: boolean
  websiteAddress?: string
}

export interface ResourceData {
  label: string | JSX.Element
  value: string | JSX.Element
}

export interface FilterColumn {
  name: string
  id: number
  type: string
  filterTarget: string
}

export interface ActionOptions {
  type: string
  label: string | JSX.Element
  callback?: () => void
  disabled?: boolean
  variant?: ButtonProps['variant']
}

export interface ResourceActions {
  icon?: string
  callback?: () => void
  options?: Array<ActionOptions>
}

export interface Image {
  name: string
  url: string
}

export interface RiskSource {
  riskTypeId: number
  headquarter?: Risk
  industry?: Risk
  operation?: AggregatedCountryRisk
  subsupplier?: AggregatedCountryRisk
}

export interface AggregatedCountryRisk {
  countryId: number
  countryName: string
  highestRisk: Risk
}

export enum RiskIndexSource {
  WoldBankGroupHumanRights = 'WoldBankGroupHumanRights',
  TransparencyInternationalCorruption = 'TransparencyInternationalCorruption',
  WriWaterRisk = 'WriWaterRisk',
  MinderooFoundationModernSlavery = 'MinderooFoundationModernSlavery',
  ItucLaborRights = 'ItucLaborRights',
  WwfSoilCondition = 'WwfSoilCondition',
  SaveTheChildrenEndOfChildHood = 'SaveTheChildrenEndOfChildHood',
  UndpHumanDevelopment = 'UndpHumanDevelopment',
  WorldfavorDeforestation = 'WorldfavorDeforestation',
  WwfAirCondition = 'WwfAirCondition',
  OurWorldInDataPrivateCivilLiberties = 'OurWorldInDataPrivateCivilLiberties',
  OurWorldInDataHumanRights = 'OurWorldInDataHumanRights',
  ClimateChangeTransitionalRisksIndustry = 'ClimateChangeTransitionalRisksIndustry',
  ClimateChangePhysicalIndustry = 'ClimateChangePhysicalIndustry',
  ClimateChangeEnergyUseIndustry = 'ClimateChangeEnergyUseIndustry',
  WasteHazardousWasteIndustry = 'WasteHazardousWasteIndustry',
  ChemicalUseIndustry = 'ChemicalUseIndustry',
  PrivacyAndDataSecurityIndustry = 'PrivacyAndDataSecurityIndustry',
  HumanRightsIndustry = 'HumanRightsIndustry',
  HealthAndSafetyIndustry = 'HealthAndSafetyIndustry',
  ChildLaborIndustry = 'ChildLaborIndustry',
  ForcedLaborIndustry = 'ForcedLaborIndustry',
  LocalCommunitiesLandUseIndustry = 'LocalCommunitiesLandUseIndustry',
  CorruptionIndustry = 'CorruptionIndustry',
  WorldfavorHealthAndSafety = 'WorldfavorHealthAndSafety',
  WorldfavorWasteCircularResourceUse = 'WorldfavorWasteCircularResourceUse',
  WorldfavorChemicalUse = 'WorldfavorChemicalUse',
  WorldfavorLocalCommunitiesLandUse = 'WorldfavorLocalCommunitiesLandUse',
  WorldfavorPrivacyAndDataSecurity = 'WorldfavorPrivacyAndDataSecurity',
  WorldfavorDiversityEquityInclusion = 'WorldfavorDiversityEquityInclusion',
  FairAndDecentWagesIndustry = 'FairAndDecentWagesIndustry',
  DiversityEquityInclusionIndustry = 'DiversityEquityInclusionIndustry',
  WorldfavorBiodiversity = 'WorldfavorBiodiversity',
  WorldfavorPollution = 'WorldfavorPollution',
  WorldfavorPhysicalClimateRisk = 'WorldfavorPhysicalClimateRisk',
  WorldfavorFairAndDecentWages = 'WorldfavorFairAndDecentWages',
}

export enum SustainabilityArea {
  Environment = 'Environment',
  Social = 'Social',
  Governance = 'Governance',
  SupplyChain = 'SupplyChain',
}

export const AnswerFlagArea = 'answerFlag'

export enum RiskContext {
  Headquarter = 'Headquarter',
  Industry = 'Industry',
  Operation = 'Operation',
  Subsupplier = 'Subsupplier',
}

export enum RiskIndexType {
  Country = 'Country',
  Industry = 'Industry',
}

export interface OrganizationRiskSetting {
  isActivated: boolean
  riskType: RiskType
  weight?: number | null
}

export interface RiskType {
  id: number
  name: string
  description: string
  source: RiskIndexSource
  sustainabilityArea: SustainabilityArea
  riskIndexType: RiskIndexType
  extremeLimit: number
  highLimit: number
  mediumLimit: number
  reviewedAt: string
  sustainabilityTopicId?: number
  sustainabilityTopic?: SustainabilityTopic
}

export interface Risk {
  id: number
  riskType: RiskType
  riskTypeId: number
  targetObjectId: number
  targetObjectType: string
  value: number
  status: RiskStatus
}

export enum RiskStatus {
  Extreme = 'FExtreme',
  High = 'EHigh',
  Medium = 'DMedium',
  Low = 'CLow',
  NotApplicable = 'ANotApplicable',
  Pending = 'BPending',
  Deactivated = 'GDeactivated',
  InsufficientData = 'HInsufficientData', // Frontend-only: assessment excluded due to <30% data coverage
}

export enum RiskStatusReadableString {
  ExtremeRisk = 'Extreme risk',
  HighRisk = 'High risk',
  MediumRisk = 'Medium risk',
  LowRisk = 'Low risk',
}

export enum CountryVariant {
  Headquarter = 'Headquarter',
  Industry = 'Industry',
  Operation = 'Operation',
  Subsupplier = 'Subsupplier',
}

export interface Country {
  id: number
  countryCode: string
  iso3166Alpha3: string
  name: string
  latitude: number
  longitude: number
  image: Image
  risks: Risk[]
  regionId?: number
  region?: { id: number; name: string }
}

export interface User {
  givenName: string
  familyName: string
  email: string
  phoneNumber: string
  position: string
  auth0UserId: string
  name: string
  id: number
  createdAt: string
  image?: Image
  type: string
  role: string
  isPublicContact: boolean
  isOwnerRestricted?: boolean
}

export interface Contact extends ResourceType {
  id: number
  createdAt: string
  creatorOrganizationId: number
  creatorUserId: number
  role: string
  user: User
  givenName: string
  familyName: string
  email: string
  userId: number
  name: string
  creatorOrganization: GenericOrganization
  type: string
}

export interface ReferralContact extends ResourceType {
  name: string
  firstName: string
  lastName: string
  email: string
  referralEmailHistory: ReferralEmailHistory[]
  creatorOrganization?: Organization
  referralCode: string
}

export interface ReferralEmailHistory {
  id: number
  referralContactId: number
  createdAt: string
  providerId: number
  isScheduled: boolean
}

export interface Invitation extends ResourceType {
  id: number
  creatorOrganization: GenericOrganization
  creatorOrganizationId: number
  creatorUserId: number
  creatorUser: User
  email: string
  userId: number
  user: User
  role: Role
  solutionType: string
  invitationStatus: string
  createdAt: string
  status: string
  type: string
  isOwnerRestricted?: boolean
}

export interface Collection {
  value: string
  displayText: string
}

export interface UploadReference {
  originalFileName: string
  id: number
  sourceObjectId: number
  sourceObjectType: string
}

export interface FileAccess {
  fileId: number
  accessOrganizationId: number
  creatorOrganizationId: number
  createdAt: string
  creatorUserId: number
  creatorUser: User
}

export interface File {
  id: number
  guid: string
  originalFileName: string
  sourceObjectId: number
  sourceObjectType: string
  accesses?: FileAccess[]
  createdAt: string
  deletedAt: string
  creatorUserId: number
  creatorUser?: User
  creatorOrganizationId: number
  creatorOrganization?: GenericOrganization
}

export interface ProviderComment {
  id: number
  objectInstanceId: number
  text: string
  createdAt: string
  creatorOrganizationId: number
  creatorOrganization?: GenericOrganization
  creatorUserId: number
  creatorUser?: User
}

export interface RequestItem {
  id: number
  questionId: number
  requestSectionId: number
  description: string
  guidance?: string
  order: number
  questionTypeId: number
  questionType: {
    name: string
  }
  template?: Question
  allowMultiChoice?: boolean
  isRequired: boolean
  unit: Unit
  unitId: number
  requestCollection: {
    id: number
    items: Collection[]
  }
  requestCollectionId: number
  section: {
    title: string
    requestId: number
  }
  questionnaireQuestionRules?: QuestionRule[]
}

export interface RequestCollectionPicker {
  id: number
  name: string
  creatorOrganizationId: number
  isGlobal: boolean
  permittedOptions: string[]
}

export interface RequestSection {
  id: number
  title: string
  description: string
  order: number
  items: RequestItem[]
}

export enum ReportTemplateState {
  None = 'None',
  Recommended = 'Recommended',
  Reported = 'Reported',
}

export enum RequestStatus {
  NotSet = 'NotSet',
  Sent = 'Sent',
  ResponseSubmitted = 'ResponseSubmitted',
  ResponseCorrectionNeeded = 'ResponseCorrectionNeeded',
  ResponseApproved = 'ResponseApproved',
  ResponseScraped = 'ResponseScraped',
}

export enum InquiryStatus {
  NotSet = 'NotSet',
  Requested = 'Requested',
  Submitted = 'Submitted',
  Scraped = 'Scraped',
  Approved = 'Approved',
  CorrectionNeeded = 'CorrectionNeeded',
  Rejected = 'Rejected',
}

export enum ConfidenceLevel {
  Low = 'Low',
  Medium = 'Medium',
  High = 'High',
}

export interface Inquiry {
  id: number
  createdAt: string
  template: QuestionnaireTemplate
  questionnaireTemplateId: number
  requestId?: number
  request?: Request
  status: InquiryStatus
  providerId: number
  provider: Provider
  deadline: string
  activatedAt: string
  isActivated: boolean
  periodStartsAt: string
  periodEndsAt: string
  solutionType: string
  creatorOrganization: GenericOrganization
  creatorOrganizationId: number
  periodType: string
  periodName: string
  targetProductId?: number
  targetProduct?: Product
  requestAutomationId?: number
  requestAutomation?: RequestAutomation
}

export interface Request {
  id: number
  tags: Array<RequestTags>
  type: typeof ResourceTypes.Request
  createdAt: string
  creatorOrganization: GenericOrganization
  creatorOrganizationId?: number
  responderOrganization: GenericOrganization
  responderId: number
  questionnaireTemplateId: number
  template: QuestionnaireTemplate
  title: string
  requestStatus: RequestStatus
  request: Request
  responseInternalStatus: InternalVerificationStatus
  /* Combined status of all verifications given by different accessors. Should only be seen by provider */
  responseExternalStatus: ExternalVerificationStatus
  periodStartsAt?: string
  periodEndsAt?: string
  periodName: string
  periodType: Periods
  solutionTypeId: number
  image?: Image
  targetObject: GenericOrganization
  targetAliasObject: GenericOrganization
  targetAliasObjectType: string
  responses?: Response[]
  sections: RequestSection[]
  responderTargetObjectType: string
  responderOrganizationId: number
  description?: string
  isPrimarySubscriber: boolean
  reportTemplateState?: ReportTemplateState
  scrapingType?: ScrapingType
  inquiries?: Inquiry[]
  subscriptions: Subscription[]
  targetProductId?: number
  targetProduct?: Product
}

export enum SolutionType {
  Default = 'Default',
  Investing = 'Investing',
  Sourcing = 'Sourcing',
  Transparency = 'Transparency',
  Management = 'Management',
  Finance = 'Finance',
  EsgReporting = 'Esg Reporting',
}
export interface SharedTemplate {
  creatorOrganizationId: number
  requestTemplateId: number
  organizationId: number
  organization: GenericOrganization
  solutionType: SolutionType
}

export interface TemplateOrganizationAvailability {
  id: number
  organizationId: number
  organization?: GenericOrganization
}

export enum QuestionnaireTypeEnum {
  Regular = 'Regular',
  Shared = 'Shared',
}

export enum ScrapingType {
  Default = 'Default',
  AdverseScraping = 'AdverseScraping',
}

export interface ReportAccessModel {
  requestId: number
  organizationIds: number[]
  isSharedWithTemplateAuthorizedOrgs: boolean
}

export interface AssignAccessorModel {
  requestId: number
  isSharedWithTemplateAuthorizedOrgs: boolean
  accessorOrganizationIds: number[]
}

export enum ExternalVerificationStatus {
  NotSet = 'NotSet',
  Approved = 'Approved',
  CorrectionNeeded = 'CorrectionNeeded',
}

export enum InternalVerificationStatus {
  NotSet = 'NotSet',
  Approved = 'Approved',
  NotApproved = 'NotApproved',
}

export interface Response {
  id: number
  creatorOrganization?: GenericOrganization
  creatorOrganizationId?: number
  creatorUserId?: number
  createdAt: string
  targetRequestId: number
  responderTargetTypeId: number
  submittedAt: string
  draftStatus: ResponseDraftStatus
  /* Combined status of all verifications given by different accessors. Should only be seen by provider */
  externalVerificationStatus: ExternalVerificationStatus
  externalVerificationComment: string
  internalVerificationStatus: InternalVerificationStatus
  internalVerificationComment: string
  responseHash: string
  responderOrganization: GenericOrganization
  responderTarget: GenericOrganization
  responderTargetObject: GenericOrganization
  request: Request
  items: ResponseItem[]
  creatorUser: Contact
  responderTargetType: string
  responderModel: string
  responderObjectId: number
  responderObject: GenericOrganization
  verificationOrganizationId: number
  /* Verifications given by each accessor. Accessors should only see their own, providers should see all */
  verifications?: Verification[]
  responseAnalytics?: ResponseAnalytics
  responseScraping?: ResponseScraping
  isScraped: boolean
  isLatestSubmitted: boolean
  dataFound?: number
}

export interface ResponseAnalytics {
  id: number
  responseId: number
  flagCount: number
}

export interface ResponseWithPreviousCorrectionNeededItem extends Response {
  items: ResponseItemWithPreviousCorrectionNeededItem[]
}

export interface ResponseItem {
  id: number
  createdAt: string
  creatorOrganizationId?: number
  creatorOrganization?: GenericOrganization
  requestItemId: number
  responseId: number
  receiverTypeId: number
  receiverObjectId: number
  receiverContactId: number
  targetTypeId: number
  isValid: boolean
  answer: number | string
  isAnswered: boolean
  isCollection: boolean
  selectedOptions: string[]
  request: Request
  response: Response
  requestItem: RequestItem
  responderObject: GenericOrganization
  responderOrganization: GenericOrganization
  responderTarget: GenericOrganization
  responderTargetObject: GenericOrganization
  uploadReferences: UploadReference[]
  responderTargetType: string
  cannotAnswer: boolean
  estimatedAnswerAt: string | null
  expiresAt: string | null
  selected: boolean
  isValueSet: boolean
  isScraped: boolean
  flag: ReportFlagType
  flagRule?: ReportFlagRule
  /* Verifications given by each accessor. Accessors should only see their own, providers should see all */
  verifications?: VerificationResponseItem[]
  answerStatus: AnswerStatus
  comment?: string
  questionAnswerClassificationResults?: QuestionAnswerClassificationResult[]
  scrapedAnswer?: ScrapedAnswer
  coordinates?: CoordinateLocation[]
}

export interface CoordinateLocation {
  id?: number
  clientId?: string // Stable key for React list rendering
  latitude?: number
  longitude?: number
  description?: string
  order: number
}

export enum EvidenceType {
  None = 'None',
  IndirectHint = 'IndirectHint',
  DescribedPractice = 'DescribedPractice',
  ExplicitStatement = 'ExplicitStatement',
}

export enum SourceScope {
  None = 'None',
  Entity = 'Entity',
  ControllingParent = 'ControllingParent',
  Other = 'Other',
}

export interface ScrapedAnswer {
  /**
   * @deprecated Use searchResults instead
   */
  citations: ScrapedAnswerCitation[]
  searchResults: SearchResult[]
  answerBackgroundSummary?: string
  evidenceSnippet?: string
  confidenceLevel?: ConfidenceLevel
  evidenceType?: EvidenceType
  sourceScope?: SourceScope
  reportingYear?: number
  basis?: string
}
export interface SearchResult {
  title: string
  url: string
  date: string
}

export interface ScrapedAnswerCitation {
  citationType: string
  text: string
  url: string
}

export interface QuestionAnswerClassificationResult {
  id: number
  responseItemId: number
  rank: QuestionAnswerRank
  questionAnswerClassificationRuleId: number
  questionAnswerClassificationRule?: QuestionAnswerClassificationRule
}

export enum AnswerStatus {
  Answered = 'Answered',
  NotApplicable = 'NotApplicable',
  NotAvailable = 'NotAvailable',
  Excluded = 'Excluded',
  Other = 'Other',
}

export interface ResponseItemWithPreviousCorrectionNeededItem extends ResponseItem {
  previousCorrectionNeededAnswer: ResponseItem
}

export type BaseVerification = {
  id: number
  createdAt: string
  creatorOrganizationId: number
  providerOrganizationId: number
  creatorOrganization?: GenericOrganization
  creatorUser?: User
  externalVerificationStatus: ExternalVerificationStatus
  externalVerificationComment: string
  internalVerificationComment: string
  internalVerificationStatus: InternalVerificationStatus
  externalVerificationStatusSetAt: string
}

export type VerificationResponseItem = BaseVerification & {
  requestItemId: number
  responseItemId: number
}

export type Verification = BaseVerification & {
  requestId: number
  responseId: number
  creatorUserId: number
}

export interface ResponseItemsVerifications {
  responseItemIds: number[]
  externalVerificationStatus: ExternalVerificationStatus
  externalVerificationComment?: string
  verifyAll?: boolean
}

export interface ReviewAll {
  responseItemIds: number[]
  externalVerificationStatus: ExternalVerificationStatus
  responseId?: number
  responseExternalVerificationComment?: string
  responseItemsExternalVerificationComment?: string
}

export interface ResponseInternalVerification {
  responseId: number
  internalVerificationStatus: InternalVerificationStatus
  internalVerificationComment?: string
}

export interface ResponseItemInternalVerification {
  responseItemIds: number[]
  internalVerificationStatus: InternalVerificationStatus
  internalVerificationComment?: string
}

export interface QuestionnaireTemplate {
  title: string
  description?: string
  id: number
  creatorOrganization: GenericOrganization
  creatorUser: User
  createdAt: string
  creatorOrganizationId: number
  creatorUserId: number
  tags: RequestTags[]
  isPublished?: boolean
  sections?: QuestionnaireTemplateSection[]
  questionnaireTemplateType: QuestionnaireTypeEnum
  sharedTemplates?: SharedTemplate[]
  image?: Image
  assessmentQuestionnaireLink?: AssessmentQuestionnaireLink[]
  isStandardReport: boolean
  allowGroupNetworkRequestSharing: boolean
  languageCode: string
  scrapingType: ScrapingType
  customSystemPrompt?: string | null
  solutionAvailability?: SolutionType[]
  organizationAvailability?: TemplateOrganizationAvailability[]
}

export interface QuestionnaireTemplateSection {
  id: number
  title: string
  description: string
  questions: QuestionInTemplate[]
  questionnaireTemplateId: number
  questionnaireTemplate?: QuestionnaireTemplate
  order: number
}

export interface QuestionInTemplate extends Question {
  isRequired: boolean
  order: number
  questionId: number
}

export interface LogIdentity {
  userId: number
  organizationId: number
}

export enum ResponseDraftStatus {
  Draft = 'Draft',
  Submitted = 'Submitted',
  Scraped = 'Scraped',
}

export type AllStatuses =
  | InquiryStatus
  | ResponseDraftStatus
  | ExternalVerificationStatus
  | InternalVerificationStatus
  | RequestStatus

export enum HistoryStatus {
  Draft = 'Draft',
  Submitted = 'Submitted',
  CorrectionNeeded = 'CorrectionNeeded',
  Approved = 'Approved',
  ResponseScraped = 'ResponseScraped',
}

export enum MappingRequestStatus {
  Requested = 'Requested',
  InProgress = 'InProgress',
  Submitted = 'Submitted',
  DueSoon = 'DueSoon',
  Overdue = 'Overdue',
}

export interface RequestHistory {
  requestId: number
  responseId: number
  status: HistoryStatus
  type: string
  id: number
  createdAt: string
  creatorOrganizationId: number
  creatorUserId?: number
  creatorUser?: User
}

export interface Snapshot {
  requestItemId: number
  responseId: number
  answer: [{ value: string; selected: boolean }]
  isValid: boolean
  isAnswered: boolean
  cannotAnswer: boolean
  isCollection: boolean
  externalVerificationStatus: ExternalVerificationStatus
  externalVerificationComment: string
  internalVerificationStatus: Indicator
  internalVerificationComment: string
  externalVerificationStatusSetAt: string
  type: string
  createdAt: string
  creatorOrganizationId: number
  creatorUserId: number
  id: number
}

export interface SolutionOrganization {
  organizationId: number
  organizationName: string
  solutionType: string
}

export interface AuditLog {
  logIdentity: LogIdentity
  serviceType: string
  sourceEventType: string
  solutionType: string
  objectType: string
  objectInstanceId: number
  beforeSnapshot: Snapshot
  afterSnapshot: Snapshot
  id: number
  creatorOrganization: GenericOrganization
  creatorUser: User
  createdAt: string
  creatorOrganizationId: number
  creatorUserId: number
  type: string
}

export interface Subscription {
  id: number
  createdAt: string
  isPrimary: boolean
  creatorOrganization: GenericOrganization
  creatorOrganizationId: number
  request: Request
  responderProviderId: number
  targetProviderId: number
  target: Provider
  targetProduct?: Product
}

export interface ResourceExplorer {
  requestTemplatesData: CollectionObjectResult<QuestionnaireTemplate> | null
  notifications: CollectionObjectResult<Notification> | null
}

export interface Indicator {
  description: string
  guidance: string
  indicatorType: 'Options' | 'Number'
  unitId: number
  unit: Unit
  section: FrameworkSection
  id: number
  createdAt: string
  creatorOrganizationId: number
  creatorUserId: number
  type: string
  frameworkOptionCollection: FrameworkOptionCollection
}

export interface FrameworkSection {
  title: string
  description: string
  guidance: string
  framework: Framework
  items: Indicator[]
  id: number
  createdAt: string
  creatorOrganizationId: number
  creatorUserId: number
  type: string
}

export interface Framework {
  title: string
  description: string
  sections: FrameworkSection[]
  id: number
  createdAt: string
  creatorOrganizationId: number
  creatorUserId: number
  type: string
}

export interface FrameworkOptionCollection {
  createdAt: string
  id: number
  type: string
  values: string[]
}

export interface DataPoint {
  periodStartsAt: string
  periodEndsAt: string
  periodName: string
  evidence: string
  solutionType: string
  indicatorId: number
  indicator: Indicator
  targetObjectId: number
  targetObjectType: string
  targetObject: GenericOrganization
  id: number
  createdAt: string
  creatorOrganizationId: number
  creatorUserId: number
  type: string
  value: string | number
}

export interface Unit {
  baseUnitFactor: number | null
  id: number
  name: string
  quantityType: string | null
  symbol: string | null
  baseUnitId: number | null
}

export interface CollectionObjectResult<T> {
  items: T[]
  status: number
  totalResult: number
  pagesCount: number
  itemsPerPage: number
  pageNumber: number
}

export interface ReferralScheduledRequests {
  templateId: number
  periodType: string
  periodStartsAt: string
  periodEndsAt: string
  dueAt: string | null
}

export interface Referral {
  code: string
  creatorOrganizationId: number
  creatorUserId: number
  expiresAt: string
  id: number
  solutionType: string
  scheduledRequests?: ReferralScheduledRequests[]
}

export interface ReferralLink {
  organizationId: number
  code: string
}

export type CurrentResource = Investee | Contact | Supplier | null

export interface SendRequestResult {
  successCount: number
  duplicateCount: number
  isScheduled: boolean
}

export interface PeriodTemplateCombination {
  templateId: number
  templateTitle: string
  periodName: string
}

export enum GoalDirectionType {
  Above = 'Above',
  Below = 'Below',
  Exact = 'Exact',
}

export interface GoalTracker {
  id: number
  questionId: number
  question: Question
  targetReference: string
  description: string
  goalValue: number
  title: string
  filterCollections: FilterGroup[]
  unitId: number
  goalValueUnit: Unit
  goalDirection: GoalDirectionType
}

export interface AssessmentLevelResult {
  level: number
  providerCount: number
}

export interface QuestionnaireStatistics {
  totalQuestionnaires: number
  totalRequests: number
  reportedRequests: number
  percentResponseRate: number
  companiesEngaged: number
}

export interface QuestionnaireRequestProgressStatistics {
  templateId: number
  numberOfRespondedRequests: number
  totalNumberOfRequestsSent: number
  completed: boolean
  templateTitle: string
}

export interface QuestionnaireCompanyProgressStatistics {
  providerId: number
  numberOfRespondedRequests: number
  totalNumberOfRequestsSent: number
  completed: boolean
  responderCompanyName: string
  responderImageUrl: string
  label: string
}

export interface ReportCreateModel {
  periodStartsAt: string
  periodEndsAt: string
  templateId: number
  inquiryId?: number
  targetProductId?: number
}

export interface InquiriesByTemplate {
  templateId: number
  templateTitle: string
  templateImageUrl?: string
  requestId?: number
  inquiryInfos: InquiryInfo[]
  periodStartsAt: string
  periodEndsAt: string
  closestDeadline?: string
  status?: InquiryStatus
  targetProductId?: number
  targetProductName?: string
}

interface InquiryInfo {
  accessorOrganizationId: number
  accessorName: string
  accessorImageUrl?: string
  inquiryId: number
}

export interface AssignSolution {
  organizationId: number
  accountType: AccountType
  isPayingAccessor: boolean
}

export interface RiskPrioritizationSummary {
  riskPrioritization: ProviderRisk
  hqCountry?: Country
}

export interface ProviderRisk {
  riskStatus: RiskStatus
  providerId: number
  provider: Provider
  periodName: string
  periodStartsAt: string
  periodEndsAt: string
  periodType: Periods
  totalEnvironmentResponses: number
  environmentFlagCount: number
  environmentRiskStatus: RiskStatus
  socialFlagCount: number
  socialRiskStatus: RiskStatus
  totalSocialResponses: number
  governanceFlagCount: number
  totalGovernanceResponses: number
  governanceRiskStatus: RiskStatus
  customFlagCount: number
  customRiskStatus: RiskStatus
  finalRisk: FinalRiskRating
}

export interface ParentObject {
  id: number
  title: string
  questionType: string
  unit: Unit
}

export interface PeriodDetails {
  periodName: string
  answer?: number | string
  cannotAnswer?: boolean
  collectionResponse?: string
  isMultiSelectOptionQuestion: boolean
}

export interface ProviderProgressView {
  id: number
  templateId: number
  requestId: number
  responseId: number
  parentObject: ParentObject
  periodDetails: PeriodDetails[]
}

export interface SuggestedResponsePayload {
  organizationInfo: {
    id: number
    name: string
  }
  message: string
}

export type RequestItemPayload = {
  organizationInfo: {
    id: number
    name: string
  }
  requestItem: RequestItem
}

export enum QuestionRuleTypes {
  Skip = 'Skip',
  Show = 'Show',
}

export interface QuestionRule {
  id?: string
  questionId: number
  questionSectionId: number
  ruleType: QuestionRuleTypes
  conditionValue: string
  sectionQuestionTargets: { targetQuestionSectionId: number; targetQuestionId: number }[]
}

export interface Question {
  id: number
  creatorOrganizationId: number
  questionText: string
  guidance: string
  howTo?: string
  kpiName: string
  aiGuidance: string
  accessorGuidance: string
  categorizations: QuestionCategorization[]
  reportFlagRules?: ReportFlagRule[]
  questionTypeId: number
  allowMultiChoice?: boolean
  unitId?: number
  unit?: Unit
  questionType: {
    name: string
  }
  requestCollectionId?: number
  requestCollection?: {
    id: number
    items: Collection[]
  }
  questionAnswerClassificationRules?: QuestionAnswerClassificationRule[]
  sections?: QuestionnaireTemplateSection[]
  tag?: string
  isStandard: boolean
  isCodeOfConduct: boolean
  questionnaireQuestionRules?: QuestionRule[]
}

export interface QuestionAnswerClassificationRule {
  id?: number
  ruleType: AnswerRuleType
  value?: AnswerClassificationValue | string
  rank: QuestionAnswerRank
  questionId?: number
}

export interface QuestionCategorization {
  categoryName: string
  values: QuestionCategoryOption[]
}

export interface QuestionCategoryOption {
  id: number
  name: string
}

export interface QuestionCategoryApi {
  id: number
  name: string
  categoryType: string
}

export type QuestionCategoryType = 'Basic' | 'Regular' | 'ESRS'

export interface QuestionCategory {
  id: number
  name: string
  categoryType: QuestionCategoryType
  customSort?: (a: string, b: string) => number
}

export enum QuestionAnswerRank {
  VeryPositive = 'VeryPositive',
  Positive = 'Positive',
  Neutral = 'Neutral',
  Negative = 'Negative',
  NegativeAdverse = 'NegativeAdverse',
  NotSet = 'NotSet',
}

export enum QuestionAnswerRankScoringType {
  Default = 'default',
  Custom = 'custom',
}

export enum AnswerClassificationValue {
  Answered = 'Answered',
  NotApplicable = 'NotApplicable',
  NotAvailable = 'NotAvailable',
  Excluded = 'Excluded',
  Other = 'Other',
}

export interface AssessmentTemplate {
  id: number
  name: string
  createdAt: string
  description: string
  level1Threshold: number
  level2Threshold: number
  level3Threshold: number
  level4Threshold: number
  levelThresholdPercentages: number[]
  level1Title: string
  level2Title: string
  level3Title: string
  level4Title: string
  level5Title: string
  level1Description: string
  level2Description: string
  level3Description: string
  level4Description: string
  level5Description: string
  questionnaireTemplateId: number
  questionnaireTemplate: QuestionnaireTemplate
  sections: AssessmentSection[]
  levels?: number
  maximumScore?: number
  assessmentType: AssessmentType
  levelNamesType: AssessmentLevelNamesType
  isPublished: boolean
  rules?: AssessmentRule[]
  solutionAvailability?: SolutionType[]
  organizationAvailability?: TemplateOrganizationAvailability[]
}

export interface AssessmentQuestion {
  questionId: number
  points?: {
    isCustom: boolean
    positive: number
    veryPositive: number
    neutral: number
    negative: number
    negativeAdverse: number
  }
  question: Question
}

export interface AssessmentSection {
  id: number
  name: string
  description: string
  level1Threshold?: number
  level2Threshold?: number
  level3Threshold?: number
  level4Threshold?: number
  assessmentSectionNumber: number
  assessmentTemplateId?: number
  assessmentTemplate?: AssessmentTemplate
  maximumScore?: number
  rules?: AssessmentRule[]
  questions: AssessmentQuestion[]
}

export interface AssessmentRule {
  id: number
  questionId: number
  question: Question
  assessmentTemplateId: number
  assessmentTemplate?: AssessmentTemplate
  assessmentSectionId: number
  value: string
  points: number
  isNeutral: boolean
  ruleType: AnswerRuleType
}

export enum AnswerRuleType {
  SelectedValue = 'SelectedValue',
  CannotAnswer = 'CannotAnswer',
  FileUploaded = 'FileUploaded',
  NumberEqualTo = 'NumberEqualTo',
  NumberGreaterThan = 'NumberGreaterThan',
  NumberLowerThan = 'NumberLowerThan',
  AnswerStatus = 'AnswerStatus',
  IsAnswerClassified = 'IsAnswerClassified',
}

export interface OrganizationAssessmentSettings {
  isActivated: boolean
  assessmentTemplate: AssessmentTemplate
}

export interface AssessmentQuestionnaireLink {
  id: number
  assessmentTemplateId: number
  questionnaireTemplateId: number
  assessmentTemplate?: AssessmentTemplate
  questionnaireTemplate?: QuestionnaireTemplate
}

export enum AssessmentType {
  Flexible = 'Flexible',
  BaselineAssessment = 'BaselineAssessment',
  StandardAssessment = 'StandardAssessment',
}

export const AssessmentTypeLabel = {
  BaselineAssessment: 'Baseline',
}

export enum AssessmentLevelNamesType {
  Levels = 'Levels',
  Intervals = 'Intervals',
  MitigationIntervals = 'MitigationIntervals',
}

export enum SuggestedContactReasonType {
  NewCompanyContact = 'NewCompanyContact',
  NewAgentContact = 'NewAgentContact',
  NewConsultantContact = 'NewConsultantContact',
  ContactHasLeftTheCompany = 'ContactHasLeftTheCompany',
  ContactIsNotResponding = 'ContactIsNotResponding',
  Other = 'Other',
}

export enum SuggestedContactStatus {
  Requested = 'Requested',
  Disapproved = 'Disapproved',
  InvitationSent = 'InvitationSent',
  InvitationAccepted = 'InvitationAccepted',
}

export type SuggestedContact = {
  id: number
  createdAt: string
  creatorOrganizationId: number
  creatorOrganization?: GenericOrganization
  creatorUser?: User
  name: string
  email: string
  status: SuggestedContactStatus
  provider: Provider
  providerId: number
  reasonType: SuggestedContactReasonType
  reasonExtraInfo?: string
  organization?: GenericOrganization
}

export interface AssessmentTemplateWithClassificationRules {
  assessmentTemplate: AssessmentTemplate
  questions?: Question[]
  sections?: AssessmentSection[]
}

export interface AssessmentQuestionnaireSyncStatus {
  isSynced: boolean
  maximumScoreIsStale: boolean
  tagsAreStale: boolean
  linksAreStale: boolean
  addedQuestions: Question[]
  removedQuestions: Question[]
  sections: { id: number; name: string | null }[]
}

export interface ExcelExportColumnsJson {
  path: string
  displayName: string
  isChecked: boolean
}

export interface ExcelExportSettingsPayload {
  resourceType: (typeof ResourceTypes)[keyof typeof ResourceTypes]
  resourceView?: string
  columnsJson: ExcelExportColumnsJson[]
}

export interface ExcelExportSettings {
  id: number
  creatorOrganizationId: number
  creatorUserId: number
  createdAt: string
  solutionType: SolutionType
  resourceType: (typeof ResourceTypes)[keyof typeof ResourceTypes]
  resourceView: string
  columnsJson: ExcelExportColumnsJson[]
  deletedAt: string
}

export enum ResponseScrapingStatus {
  InProgress = 'InProgress',
  Completed = 'Completed',
  Failed = 'Failed',
}

export interface ResponseScraping {
  id: number
  creatorOrganizationId: number
  startedAt: string
  type: string
  status: ResponseScrapingStatus
  solutionType: string
  providerId: number
  questionnaireId: number
  percentageFound?: number
  responseId?: number
  periodName: string
  response?: Response
  questionnaireTemplate: QuestionnaireTemplate
  provider: Provider
  requestAutomationId?: number
  requestAutomation?: RequestAutomation
}

export enum RequestTargetType {
  Product = 'Product',
  Provider = 'Provider',
}
